import { Injectable } from '@angular/core';
import {
	HttpInterceptor,
	HttpHeaders,
	HttpRequest,
	HttpHandler,
	HttpEvent,
	HttpErrorResponse,
	HttpResponse
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { SecurityService } from '../services/security.service';
import { AppInsightsService } from '../services/app-insights.service';
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root'
})
export class AppHttpInterceptor implements HttpInterceptor {
	// Add any URLs that should be ignored by the interceptor
	private ignoreUrls: string[] = [
		environment.apiUrl + 'api/AppIdentity/Login',
		environment.apiUrl + 'api/AppIdentity/VerifyEmail',
		environment.apiUrl + 'api/AppIdentity/SendForgotPassword',
		environment.apiUrl + 'api/AppIdentity/ResetPassword',
		environment.apiUrl + 'api/Main/GetIcon',
		'https://atlas.microsoft.com' // Azure Maps - uses its own authentication
	];

	private ignore500Urls: string[] = [
		environment.apiUrl + 'api/Main/UploadUsers',
		environment.apiUrl + 'api/Contract/GetPremiumsFromMEDES',
		environment.apiUrl + 'api/Contract/GetPremiumsFromFAMIS',
		environment.apiUrl + 'api/Contract/DownloadDocument',
		environment.apiUrl + 'api/Contract/DownloadDocumentById',
		environment.apiUrl + 'api/Contract/DownloadDocumentBatchById',
		environment.apiUrl + 'api/Contract/SaveDocumentPdfData'
	];

	// Add any URLs called without user interaction.  They should not prevent the app from timing out
	private silentURLs: string[] = [
		environment.apiUrl + 'api/AppIdentity/RefreshToken',
		environment.apiUrl + 'api/Main/GetIcon',
		'https://login.microsoftonline.com'
	];

	constructor(
		private router: Router,
		private securityService: SecurityService,
		private appInsightsService: AppInsightsService
	) {}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Keep track of every http request to prevent application from timing out (unless it's in the silent list)
		if (
			!this.silentURLs.some((x) => {
				return request.url.startsWith(x);
			})
		) {
			this.securityService.actionTaken();
		}

		// If the requested URL is not in the ignore list then add headers for the bearer token and selected application
		if (
			!this.ignoreUrls.some((x) => {
				return request.url.startsWith(x);
			})
		) {
			return this.securityService.acquireToken()?.pipe(
				mergeMap((result) => {
					const newHeaders = this.createNewHeaders(request, result.accessToken);
					const authReq = request.clone({ headers: newHeaders });

					return next.handle(authReq)?.pipe(
						tap(
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							(event: HttpEvent<any>) => {
								if (event instanceof HttpResponse) {
									// Event handling placeholder
								}
							},
							(err: unknown) => {
								if (err instanceof HttpErrorResponse) {
									if (err.status === 401) {
										if (this.securityService.isAuthenticated()) {
											this.router.navigate(['error/401']);
										} else {
											this.router.navigate(['security/login']);
										}
									} else if (err.status === 403) {
										this.router.navigate(['error/403']);
									} else if (
										err.status === 500 &&
										!this.ignore500Urls.some((x) => {
											return err.url.startsWith(x);
										})
									) {
										this.router.navigate(['error/500']);
									}
								}
							}
						)
					);
				})
			);
		} else {
			return next.handle(request);
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private createNewHeaders(request: HttpRequest<any>, token: string) {
		const headerSettings: Record<string, string | string[]> = {};
		for (const key of request.headers.keys()) {
			headerSettings[key] = request.headers.getAll(key);
		}

		// Add Authorization header with Bearer Token
		if (token) {
			headerSettings['Authorization'] = 'Bearer ' + token;
		}

		// Add the selected application id
		if (this.securityService.getCurrentApp()) {
			headerSettings['x-ht-appId'] = this.securityService.getCurrentApp().application.applicationId.toString();
		}

		// Let the API join its telemetry to this browser session
		const sessionId = this.appInsightsService.getSessionId();
		if (sessionId) {
			headerSettings['x-ht-sessionId'] = sessionId;
		}

		const newHeader = new HttpHeaders(headerSettings);

		return newHeader;
	}
}
